/**
 * MODULE: domain/sales-intelligence/frontend/context/SalesIntelligenceContext.tsx
 * PURPOSE: Global context for opening Sales Intelligence modal from anywhere
 *
 * DEPENDENCIES:
 *   - React: createContext, useContext, useRef, useState, useCallback
 *   - ./components/SalesIntelligenceModal
 *
 * PUBLIC API:
 *   - SalesIntelligenceProvider: Context provider component
 *   - useSalesIntelligence: Hook to open/close the modal
 *
 * AGENT SAFETY: SAFE
 * FILE SIZE: 80 / 200 lines
 *
 * See: /domain/sales-intelligence/README.md
 */

import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { SalesIntelligenceModal } from '../components/SalesIntelligenceModal';

export interface OpenSalesIntelligenceOptions {
  /** Company name to prefill in the chat */
  company?: string | null;
  /** Called when user saves research to account (e.g. refetch list) */
  onSaveToAccount?: () => void;
}

interface SalesIntelligenceContextValue {
  openSalesIntelligence: (options?: OpenSalesIntelligenceOptions) => void;
  closeSalesIntelligence: () => void;
}

const SalesIntelligenceContext = createContext<SalesIntelligenceContextValue | null>(null);

export function SalesIntelligenceProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [prefillCompany, setPrefillCompany] = useState<string | null>(null);
  const onSaveToAccountRef = useRef<(() => void) | null>(null);

  const openSalesIntelligence = useCallback((options?: OpenSalesIntelligenceOptions) => {
    setPrefillCompany(options?.company ?? null);
    onSaveToAccountRef.current = options?.onSaveToAccount ?? null;
    setOpen(true);
  }, []);

  const closeSalesIntelligence = useCallback(() => {
    setOpen(false);
    setPrefillCompany(null);
    onSaveToAccountRef.current = null;
  }, []);

  const handleSaveToAccount = useCallback(() => {
    onSaveToAccountRef.current?.();
    closeSalesIntelligence();
  }, [closeSalesIntelligence]);

  const value: SalesIntelligenceContextValue = {
    openSalesIntelligence,
    closeSalesIntelligence,
  };

  return (
    <SalesIntelligenceContext.Provider value={value}>
      {children}
      <SalesIntelligenceModal
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) closeSalesIntelligence();
        }}
        prefillCompany={prefillCompany}
        onSaveToAccount={onSaveToAccountRef.current ? handleSaveToAccount : undefined}
      />
    </SalesIntelligenceContext.Provider>
  );
}

export function useSalesIntelligence(): SalesIntelligenceContextValue {
  const ctx = useContext(SalesIntelligenceContext);
  if (!ctx) {
    throw new Error('useSalesIntelligence must be used within SalesIntelligenceProvider');
  }
  return ctx;
}

/** Safe hook - returns null if outside provider */
export function useSalesIntelligenceOptional(): SalesIntelligenceContextValue | null {
  return useContext(SalesIntelligenceContext);
}
